import { useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import SMS_POPUP_NAME from 'constants/smsPopUpName.const';
import { useNcobRedirectUrl } from 'hooks/useNcobRedirectUrl.hook';
import { usePathname } from 'hooks/usePathname.hook';
import { useTranslation } from 'providers/GILT.provider';
import selectEffectiveUserRole from 'selectors/selectEffectiveUserRole';
import { isEffectiveReceptionist } from 'selectors/user';
import fromAnyToString from 'utils/converters/fromAnyToString';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';

import {
  getUniqClients as getUniqClientsAction,
  purchaseSmsBundle as purchaseSmsBundleAction,
  sendMarketingSMS as sendMarketingSMSAction,
  setMessageSent as setMessageSentAction,
  setSmsMessage as setSmsMessageAction,
} from 'clientCommunications/actions';
import NotSmsCreditDialog from 'clientCommunications/components/Clients/NotSmsCreditDialog';
import PopupWith2Buttons from 'clientCommunications/components/Clients/PopupWith2Buttons';
import PurchaseSmsCreditDialog from 'clientCommunications/components/Clients/PurchaseSmsCreditDialog';
import SmsComposeAndSend from 'clientCommunications/components/Clients/SmsComposeAndSend';
import SmsSentDialog from 'clientCommunications/components/Clients/SmsSentDialog';
import { getSalonOnlineSettings as getSalonOnlineSettingsAction } from 'settings/online-booking/actions';

const SmsButtonNew = ({
  showParentSmsPopup = alwaysNoop,
  procedure = null,
  clientId = 0,
  duplicated = 0,
  optoutNote = '',
  bookingID = 0,
  clientName = '',
  isOpen: isOpenProp = false,
  smsComposeModalOpen: smsComposeModalOpenProp = false,
  moveNotify = false,
  cancelled = false,
  bucketType,
  hideElement,
  customTitle,
  numClientsToBeSentOptout,
  procedureDragFrom,
  stylists,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const pathname = usePathname();
  const { t } = useTranslation();

  // Redux selectors
  const salonId = useSelector((state) => state.currentSalon.id);
  const salonName = useSelector((state) => state.currentSalon.name);
  const has_billing = useSelector((state) => state.currentSalon.has_billing);
  const permission = useSelector(selectEffectiveUserRole);
  const isReceptionist = useSelector(isEffectiveReceptionist);
  const numOfMessages = useSelector((state) => state.clientCommunications.numOfMessages);
  const smsMessage = useSelector((state) => state.clientCommunications.smsMessage);
  const errorMessage = useSelector((state) => state.clientCommunications.errorMessage);
  const messageSent = useSelector((state) => state.clientCommunications.messageSent);
  const isFetching = useSelector((state) => state.clientCommunications.isFetching);
  const ncobTinyUrl = useSelector((state) => state.settings.onlineBooking.settingsData.ncob_tiny_url);
  const effectiveNcobUrl = useNcobRedirectUrl({ ncobTinyUrl, salonId });
  const online_booking = useSelector((state) => state.currentSalon.online_booking);
  const salonOnlineBooking = useSelector((state) => state.settings.onlineBooking.settingsData.online_booking);
  const consultation_link = useSelector((state) => state.currentSalon.consultation_link);
  const unique_recipients_count_redux = useSelector((state) => state.clientCommunications.unique_recipients_count);
  const phone = useSelector((state) => state.currentSalon.phone);
  const displayStylists = useSelector((state) => state.scheduler.displayStylists);
  const sending_allowed = useSelector((state) => state.clientCommunications.sending_allowed);
  const sms_credits_available = useSelector((state) => state.clientCommunications.sms_credits_available);
  const expected_count = useSelector((state) => state.clientCommunications.expected_count);
  const clientHasNoPhone = useSelector((state) => state.clientCommunications.clientHasNoPhone);

  // State
  const [beforeSendPopupOpen, setBeforeSendPopupOpen] = useState(false);
  const [billingPopupOpen, setBillingPopupOpen] = useState(false);
  const [finalAgreeToSend, setFinalAgreeToSend] = useState(false);
  const [exitPopupOpen, setExitPopupOpen] = useState(false);
  const [smsComposeModalOpen, setSmsComposeModalOpen] = useState(smsComposeModalOpenProp || false);
  const [smsSentModalOpen, setSmsSentModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [unique_recipients_count, setUniqueRecipientsCount] = useState(0);
  const [salonNotSmsCreditPopup, setSalonNotSmsCreditPopup] = useState(false);
  const [purchaseModal, setPurchaseModal] = useState(false);

  const initialNotifyMessageSetRef = useRef(false);
  const currentStylistRef = useRef(procedure?.stylist?.user_name);
  const prevSmsComposeModalOpenPropRef = useRef(smsComposeModalOpenProp);

  // Dispatch wrappers
  const dispatchSetSmsMessage = useCallback((text, num) => dispatch(setSmsMessageAction(text, num)), [dispatch]);

  const dispatchGetUniqClients = useCallback((...args) => dispatch(getUniqClientsAction(...args)), [dispatch]);

  const dispatchPurchaseSmsBundle = useCallback((...args) => dispatch(purchaseSmsBundleAction(...args)), [dispatch]);

  // Handlers
  const handleOpen = useCallback(
    (tf, fromWhere) => {
      // we don't need to close compose after purchase
      if (!fromWhere) {
        setIsOpen(tf);
        setSmsComposeModalOpen(tf);
      }
      if ((fromWhere === 'success' || fromWhere === 'exit-totally') && showParentSmsPopup) {
        showParentSmsPopup(false);
        dispatch(setSmsMessageAction('', 0));
      }
      if (!tf) {
        setSmsSentModalOpen(false);
        setBeforeSendPopupOpen(false);
        setPurchaseModal(false);
      }
    },
    [dispatch, showParentSmsPopup],
  );

  const showPopup = useCallback(
    (popup) => {
      switch (popup) {
        case 'beforeSendPopupOpen':
          setBeforeSendPopupOpen(true);
          break;
        case 'billingPopupOpen':
          setBillingPopupOpen(true);
          break;
        case 'exitPopupOpen':
          setExitPopupOpen(true);
          setSmsComposeModalOpen(false);
          break;
        case 'smsComposeModalOpen':
        case SMS_POPUP_NAME.smsCompose:
          setSmsComposeModalOpen(true);
          break;
        case 'smsSentModalOpen':
          setSmsSentModalOpen(true);
          break;
        case 'salonNotSmsCreditPopup':
          setSalonNotSmsCreditPopup(true);
          break;
        case 'purchaseModal':
          setPurchaseModal(true);
          break;
        default:
          break;
      }
      if (popup === 'billingPopupOpen') {
        handleOpen(false);
        setTimeout(() => {
          handleOpen(true);
        }, 3000);
      }
      handleOpen(true);
    },
    [handleOpen],
  );

  const closePopup = useCallback(
    (popup) => {
      switch (popup) {
        case 'beforeSendPopupOpen':
          setBeforeSendPopupOpen(false);
          break;
        case 'billingPopupOpen':
          setBillingPopupOpen(false);
          break;
        case 'exitPopupOpen':
          setExitPopupOpen(false);
          break;
        case 'smsComposeModalOpen':
          setSmsComposeModalOpen(false);
          break;
        case 'smsSentModalOpen':
          setSmsSentModalOpen(false);
          break;
        case 'salonNotSmsCreditPopup':
          setSalonNotSmsCreditPopup(false);
          break;
        case 'purchaseModal':
          setPurchaseModal(false);
          break;
        default:
          break;
      }
      if (popup !== 'exitPopupOpen') {
        handleOpen(false);
      }
      if (popup === 'billingPopupOpen' || popup === 'smsSentModalOpen') {
        dispatch(setSmsMessageAction('', 0));
        if (showParentSmsPopup) {
          // we need to tell parent to close the sms popup
          showParentSmsPopup(false);
        }
      }
    },
    [dispatch, handleOpen, showParentSmsPopup],
  );

  const closePopupExit = useCallback(() => {
    setExitPopupOpen(false);
  }, []);

  const openPurchaseModal = useCallback(() => {
    setPurchaseModal(true);
    setSmsSentModalOpen(false);
    setBeforeSendPopupOpen(false);
  }, []);

  const toggleFinalAgreeToSend = useCallback(() => {
    setFinalAgreeToSend((prev) => !prev);
  }, []);

  const gotoBilling = useCallback(() => {
    const oldpath = pathname;
    let newPath;
    if (oldpath.indexOf('day') > 0) {
      newPath = `${oldpath.split('day/')[0]}settings/billing`;
    } else if (oldpath.indexOf('communications') > 0) {
      newPath = `${oldpath.split('communications/')[0]}settings/billing`;
    } else {
      newPath = `${oldpath.split('week/')[0]}settings/billing`;
    }
    history.push({ pathname: newPath });
  }, [pathname, history]);

  const sendSms = useCallback(() => {
    const clientBucketType = bucketType;
    const bookingId = bookingID;
    const appointment = procedure?.appointment;
    const client = appointment?.client;

    const recipients = client ? [client?.id] : clientId;
    const appointmentId = bookingId ? bookingId : appointment?.id ? appointment?.id : void 1;

    const messageBucketType = clientBucketType === 'list' ? 'all' : clientBucketType === 'first' ? 'first_time' : clientBucketType === 'cancelations' ? 'canceled_last_month' : clientBucketType;

    if (!messageSent) {
      dispatch(
        sendMarketingSMSAction(
          {
            recipients,
            text: smsMessage,
            numOfMessages,
            bucket: messageBucketType,
            appointment: appointmentId,
            title: '',
          },
          fromAnyToString(salonId),
        ),
      );
    }
  }, [bucketType, bookingID, procedure, clientId, messageSent, smsMessage, numOfMessages, salonId, dispatch]);

  const doSend = useCallback(() => {
    sendSms();
  }, [sendSms]);

  const getPopupConentBeforeSend = useCallback(() => {
    if (sending_allowed) {
      if (duplicated && duplicated > 0) {
        return (
          <div>
            {t('scheduler.smsButton.usage', {
              numOfTexts: expected_count,
              texts: expected_count > 1 ? t('scheduler.smsButton.texts') : t('scheduler.smsButton.text'),
              numOfClients: unique_recipients_count,
              clients: unique_recipients_count > 1 ? t('scheduler.smsButton.clients') : t('scheduler.smsButton.client'),
            })}
          </div>
        );
      }
      if (unique_recipients_count === 0) {
        return <div>{t('scheduler.smsButton.textError')}</div>;
      } else {
        return (
          <div>
            {t('scheduler.smsButton.usage', {
              numOfTexts: expected_count,
              texts: expected_count > 1 ? t('scheduler.smsButton.texts') : t('scheduler.smsButton.text'),
              numOfClients: unique_recipients_count,
              clients: unique_recipients_count > 1 ? t('scheduler.smsButton.clients') : t('scheduler.smsButton.client'),
            })}
          </div>
        );
      }
    }

    return (
      <div>
        {clientHasNoPhone ? (
          <>{t('scheduler.smsButton.textError')}</>
        ) : (
          <>
            {t('scheduler.smsButton.creditsError', {
              count: expected_count,
              credits: sms_credits_available,
            })}
            <div style={{ fontSize: '0.7em', marginTop: '20px' }}>{t('scheduler.smsButton.purchaseMore')}</div>
          </>
        )}
      </div>
    );
  }, [sending_allowed, duplicated, expected_count, unique_recipients_count, clientHasNoPhone, sms_credits_available, t]);

  const uniqNoteConentBeforeSend = useCallback(() => {
    if (sending_allowed) {
      if (duplicated && duplicated > 0) {
        return <div>{t('scheduler.smsButton.excludedDupes')} 🙌</div>;
      }
      return '';
    }
    return '';
  }, [sending_allowed, duplicated, t]);

  // componentDidMount equivalent
  useEffect(() => {
    showPopup(SMS_POPUP_NAME.smsCompose);
    dispatch(getSalonOnlineSettingsAction(salonId));

    if (procedure && moveNotify && !initialNotifyMessageSetRef.current) {
      const cName = procedure.appointment.client.user_name;
      const appointmentDate = moment(procedure.start_time).format('ddd, Do MMM');
      const appointmentTime = moment(procedure.start_time).format('h:mm a');
      initialNotifyMessageSetRef.current = true;
      const stylistId = procedureDragFrom;

      let stylist;
      if (displayStylists.length > 1) {
        // eslint-disable-next-line prefer-destructuring
        stylist = stylists.filter((s) => s.id === stylistId)[0];
      }
      if (displayStylists.length === 1 || (stylist && !stylist.serves_as_waitlist)) {
        dispatchSetSmsMessage(
          t('scheduler.smsButton.movedMessage', {
            clientName: cName,
            salonName,
            appointmentDate,
            appointmentTime,
          }),
          1,
        );
      } else {
        dispatchSetSmsMessage(
          t('scheduler.smsButton.confirmedMessage', {
            clientName: cName,
            salonName,
            appointmentDate,
            appointmentTime,
            phone,
          }),
          1,
        );
      }
    }
    if (procedure && cancelled && !initialNotifyMessageSetRef.current) {
      const cName = procedure.appointment.client.user_name;
      const appointmentDate = moment(procedure.start_time).format('ddd, Do MMM');
      initialNotifyMessageSetRef.current = true;
      if (online_booking) {
        dispatchSetSmsMessage(
          t('scheduler.smsButton.cancelledLinkMessage', {
            clientName: cName,
            salonName,
            appointmentDate,
            ncobTinyUrl: effectiveNcobUrl,
          }),
          1,
        );
      } else {
        dispatchSetSmsMessage(
          t('scheduler.smsButton.cancelledPhoneMessage', {
            clientName: cName,
            salonName,
            appointmentDate,
            phone,
          }),
          1,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect for messageSent (replaces UNSAFE_componentWillReceiveProps messageSent logic)
  useEffect(() => {
    if (messageSent) {
      setSmsSentModalOpen(true);
      dispatch(setMessageSentAction(null));
    }
  }, [messageSent, dispatch]);

  // Effect for unique_recipients_count from redux
  useEffect(() => {
    if (unique_recipients_count_redux) {
      setUniqueRecipientsCount(unique_recipients_count_redux);
    }
  }, [unique_recipients_count_redux]);

  // Effect for smsComposeModalOpen prop changes
  useEffect(() => {
    if (smsComposeModalOpenProp !== prevSmsComposeModalOpenPropRef.current) {
      prevSmsComposeModalOpenPropRef.current = smsComposeModalOpenProp;
      setSmsComposeModalOpen(smsComposeModalOpenProp);
      if (smsComposeModalOpenProp) {
        handleOpen(true);
      } else {
        handleOpen(false);
      }
    }
  }, [smsComposeModalOpenProp, handleOpen]);

  // Effect for procedure + moveNotify (replaces UNSAFE_componentWillReceiveProps moveNotify logic)
  useEffect(() => {
    if (procedure && moveNotify && !initialNotifyMessageSetRef.current) {
      const cName = procedure.appointment.client.user_name;
      const appointmentDate = moment(procedure.start_time).format('ddd, Do MMM');
      const appointmentTime = moment(procedure.start_time).format('h:mm a');
      initialNotifyMessageSetRef.current = true;
      const stylistId = procedureDragFrom;
      const isSingleStylist = displayStylists.length === 1;
      let stylist;
      if (!isSingleStylist) {
        // eslint-disable-next-line prefer-destructuring
        stylist = stylists.filter((s) => s.id === stylistId)[0];
      }
      if (isSingleStylist || (stylist && !stylist.serves_as_waitlist)) {
        dispatchSetSmsMessage(
          t('scheduler.smsButton.confirmedStylistMessage', {
            clientName: cName,
            salonName,
            appointmentDate,
            appointmentTime,
            currentStylist: currentStylistRef.current,
          }),
          1,
        );
      } else {
        dispatchSetSmsMessage(
          t('scheduler.smsButton.replyMessage', {
            clientName: cName,
            salonName,
            appointmentDate,
            appointmentTime,
            phone,
          }),
          1,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedure, moveNotify]);

  // sendSmsFrom => Check where sms is sent from.
  // Whether from booking card and feed or from marketing clients
  // then hide some text, checkbox for agree and duplicate button NEXT

  const sendSmsFrom = (procedure && procedure.appointment && procedure.appointment.id) || bookingID;
  const showHideElmClass = hideElement ? 'hidden' : sendSmsFrom ? 'sms-from-calendar hidden' : 'sms-from-marketing';

  const title = clientName ? (customTitle ? t('scheduler.smsButton.sendMessage', { clientName }) : t('scheduler.smsButton.replyTo', { clientName })) : t('scheduler.smsButton.composeMessage');

  const popupContentBilling = t('scheduler.smsButton.noBillingDetails');
  const popupContentExit = t('scheduler.smsButton.exitMessage');

  const recipients = procedure?.appointment?.client ? [procedure?.appointment?.client?.id] : clientId;
  const appointmentId = bookingID ? bookingID : procedure?.appointment?.id ? procedure?.appointment?.id : void 1;

  return (
    <div data-bem="SmsButtonNew">
      <ReactModal
        isOpen={isOpen || (isOpenProp && smsComposeModalOpen)}
        closeTimeoutMS={0}
        ariaHideApp
        shouldCloseOnOverlayClick
        role="dialog"
        style={{ overlay: { zIndex: 500 } }}
        className={{
          base: 'dialog-container-marketing dark-class',
          afterOpen: 'dialog-container',
          beforeClose: 'dialog-container',
        }}
      >
        {billingPopupOpen && (
          <PopupWith2Buttons
            showPopup={showPopup}
            closePopup={closePopup}
            gotoBilling={gotoBilling}
            popupContent={popupContentBilling}
            billing
            finalAgreeToSend={false}
            fromAppointment
            handleOpen={handleOpen}
            isReceptionist={isReceptionist}
          />
        )}
        {beforeSendPopupOpen && (
          <PopupWith2Buttons
            isFetching={isFetching}
            messageSent={messageSent}
            showPopup={showPopup}
            closePopup={closePopup}
            doSend={doSend}
            popupContent={getPopupConentBeforeSend()}
            numOfMessages={numOfMessages}
            beforeSend
            sending_allowed={sending_allowed}
            setFinalAgreeToSend={toggleFinalAgreeToSend}
            finalAgreeToSend={finalAgreeToSend}
            uniqNote={uniqNoteConentBeforeSend()} // {uniqNote}
            optoutNote={optoutNote}
            duplicated={duplicated}
            numClientsToBeSentOptout={numClientsToBeSentOptout}
            fromAppointment={bucketType === 'appointment'}
            handleOpen={handleOpen}
            openPurchaseModal={openPurchaseModal}
            sendSMSVisibility={unique_recipients_count === 0 ? 'hidden' : ' '}
          />
        )}
        {smsComposeModalOpen && (
          <SmsComposeAndSend
            title={title}
            ButtonText={t('common.buttons.next')}
            onlineBooking={salonOnlineBooking}
            salonId={salonId}
            history={history}
            closePopup={closePopup}
            showPopup={showPopup}
            composeSMS
            setSmsMessage={dispatchSetSmsMessage}
            smsMessage={smsMessage}
            messageSent={messageSent}
            getUniqClients={dispatchGetUniqClients}
            currClientList={recipients}
            bucketType={bucketType}
            handleOpen={handleOpen}
            appointmentId={appointmentId}
            ncobTinyUrl={effectiveNcobUrl}
            has_billing={has_billing}
            isModalAction={false}
            consultation_link={consultation_link}
            purchaseModal={purchaseModal}
            showHideElmClass={showHideElmClass}
          />
        )}
        {exitPopupOpen && (
          <div className="click-overlay">
            <PopupWith2Buttons closePopup={closePopupExit} showPopup={showPopup} popupContent={popupContentExit} exit fromAppointment handleOpen={handleOpen} />
          </div>
        )}
        {smsSentModalOpen && (
          <SmsSentDialog
            closePopup={closePopup}
            smsSent
            fromAppointment
            handleOpen={handleOpen}
            sms_credits_available={sms_credits_available}
            backToCalendar={() => handleOpen(false, 'success')}
            getMoreCredit={openPurchaseModal}
          />
        )}
        {/* flow 2 salon does not have enough SMS credits */}
        {/* change this to true salonNotSmsCreditPopup to see new modals */}
        {salonNotSmsCreditPopup && (
          <NotSmsCreditDialog
            history={history}
            isModalAction
            sms_credits_available={sms_credits_available}
            openPurchaseModal={openPurchaseModal}
            expected_count={expected_count}
            numClientsToBeSentOptout={numClientsToBeSentOptout}
          />
        )}
        {purchaseModal && (
          <PurchaseSmsCreditDialog
            showPopup={showPopup}
            closePopup={closePopup}
            purchaseSmsBundle={dispatchPurchaseSmsBundle}
            salonId={salonId}
            isFetching={isFetching}
            sms_credits_available={sms_credits_available}
            errorMessage={errorMessage}
            gotoBilling={gotoBilling}
            handleOpen={handleOpen}
            permission={permission}
            variantButton="normal"
          />
        )}
      </ReactModal>
    </div>
  );
};

// this extra export is needed for tests that provide their own mock for connect()
export { SmsButtonNew as SmsButtonNew4Test };

export default SmsButtonNew;
